import { z } from 'zod';
/**
 * Wire schemas for `POST /public/leads/*` — admin-arch §3.13,
 * decisions §17 (Pro lead-capture + tariff-change request).
 * Wave 2.41 BE-2 (TASK-102).
 *
 * Both endpoints are public, write-only. No auth, no read surface
 * exposed to the public path — the lead lands in `internal.leads`
 * and the sales team picks it up via the admin "Leads" module
 * (admin-arch §3 — admin surface deferred to a later wave).
 */
/**
 * Email — basic RFC-shaped validation. Trim before validation to absorb
 * leading/trailing whitespace from copy-paste.
 *
 * Lower-cased on the wire? No — keep the user's casing for the lead
 * record (sales may want to mail back to the exact address provided).
 * The DB has a `lower(email)` index for the search side.
 */
const EMAIL_MAX = 254; // RFC 5321 path limit.
export const leadEmailSchema = z
    .string()
    .trim()
    .min(3)
    .max(EMAIL_MAX)
    .email('Invalid email format');
/**
 * Phone — digits with an optional leading `+`, 10..15 chars total
 * (brief §"Validation"). We accept the user's input as-is once it
 * matches; no E.164 normalisation here because the sales follow-up
 * is human-driven and we want to preserve the exact string the
 * prospect typed.
 */
export const leadPhoneSchema = z
    .string()
    .trim()
    .regex(/^\+?\d{10,15}$/, 'Phone must be 10-15 digits with optional leading +');
const NAME_MAX = 200;
const COMPANY_MAX = 200;
const MESSAGE_MAX = 2000;
const CAPTCHA_MAX = 4096;
const TARIFF_CODE_MAX = 64;
const TARIFF_CODE_REGEX = /^[a-z0-9_-]{2,64}$/i;
const expectedLocationsSchema = z
    .number()
    .int()
    .positive()
    .max(100_000)
    .optional();
/**
 * Tariff code shape — same loose alphanumeric guard the admin tariffs
 * endpoints use. The actual existence check is deferred to the sales
 * follow-up (decisions §18 — all tariff transitions are manual); we
 * only refuse obviously garbage tokens here.
 */
const tariffCodeSchema = z
    .string()
    .trim()
    .min(2)
    .max(TARIFF_CODE_MAX)
    .regex(TARIFF_CODE_REGEX, 'Tariff code must match [a-z0-9_-]{2,64}');
const captchaTokenSchema = z
    .string()
    .trim()
    .min(1)
    .max(CAPTCHA_MAX)
    .optional();
// ---------------------------------------------------------------------------
// POST /public/leads/pro (decisions §17 flow lines 703-710).
//
// Pre-sales form on the landing page. We collect the bare minimum needed
// for a sales-team follow-up call; `expected_locations_count` is the only
// type-specific field that materially routes the lead (small chain vs
// large network) — everything else lives in `payload`.
// ---------------------------------------------------------------------------
export const publicLeadProRequestSchema = z
    .object({
    email: leadEmailSchema,
    phone: leadPhoneSchema,
    name: z.string().trim().min(1).max(NAME_MAX),
    company: z.string().trim().min(1).max(COMPANY_MAX).optional(),
    message: z.string().trim().min(1).max(MESSAGE_MAX).optional(),
    expected_locations_count: expectedLocationsSchema,
    captcha_token: captchaTokenSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// POST /public/leads/tariff-change (decisions §17 flow lines 713-718).
//
// Existing-client form; the sales team uses `current_tariff_code` +
// `desired_tariff_code` to triage the request and run the manual upgrade /
// downgrade wizard (decisions §18). The two tariff codes are required —
// without them the lead is not actionable.
// ---------------------------------------------------------------------------
export const publicLeadTariffChangeRequestSchema = z
    .object({
    email: leadEmailSchema,
    phone: leadPhoneSchema,
    name: z.string().trim().min(1).max(NAME_MAX),
    company: z.string().trim().min(1).max(COMPANY_MAX).optional(),
    message: z.string().trim().min(1).max(MESSAGE_MAX).optional(),
    current_tariff_code: tariffCodeSchema,
    desired_tariff_code: tariffCodeSchema,
    captcha_token: captchaTokenSchema,
})
    .strict()
    .superRefine((dto, ctx) => {
    if (dto.current_tariff_code === dto.desired_tariff_code) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['desired_tariff_code'],
            message: 'desired_tariff_code must differ from current_tariff_code',
        });
    }
});
// ---------------------------------------------------------------------------
// POST /public/leads/demo — the "две недели бесплатно" form on the marketing
// landing (themusichead.com).
//
// The landing asks a venue owner for four things and nothing else: venue name,
// city, one contact, venue type. Note what is NOT here: `email` and the strict
// `leadPhoneSchema` that both `pro` and `tariff-change` require. The form
// accepts a phone OR a Telegram handle in a single field, so `contact` is a
// free-form string — normalising it would only reject valid input, and the
// follow-up is a human calling the number they were given.
// ---------------------------------------------------------------------------
const VENUE_NAME_MAX = 200;
const CITY_MAX = 120;
const CONTACT_MAX = 200;
const HONEYPOT_MAX = 200;
/** Venue kinds offered by the landing's select (landing copy v3 §9). */
export const leadVenueTypeSchema = z.enum([
    'coffee_shop',
    'restaurant',
    'restaurant_bar',
    'bar',
]);
export const publicLeadDemoRequestSchema = z
    .object({
    venue_name: z.string().trim().min(1).max(VENUE_NAME_MAX),
    city: z.string().trim().min(1).max(CITY_MAX).optional(),
    contact: z.string().trim().min(3).max(CONTACT_MAX),
    venue_type: leadVenueTypeSchema,
    /**
     * Honeypot. The landing renders it hidden and empty; a human never fills
     * it in, a form-spamming bot fills every field it finds. Declared here
     * because the object is `.strict()` — an undeclared key would 400 and
     * tell the bot exactly which field gave it away. Non-empty values are
     * dropped silently by `LeadsService`, which answers `{ok: true}` all the
     * same.
     */
    website: z.string().max(HONEYPOT_MAX).optional(),
})
    .strict();
/**
 * Public response shape — deliberately minimal. We do not leak the
 * `internal.leads.id` (no need for the public caller) nor any internal
 * metadata. The FE renders a "thank you" screen on `{ ok: true }`.
 */
export const publicLeadResponseSchema = z.object({
    ok: z.literal(true),
});
//# sourceMappingURL=lead.js.map